export type JsonValue = null | boolean | number | string | JsonValue[] | JsonObject;
export interface JsonObject {
  [key: string]: JsonValue;
}

/** Parse before rendering: JSON.parse alone silently accepts duplicate object keys. */
export function parseBoundedJson(
  source: string,
  limits: { bytes: number; depth: number; nodes: number },
): JsonValue {
  if (typeof source !== 'string' || new TextEncoder().encode(source).length > limits.bytes)
    throw new Error('JSON exceeds the supported byte limit.');
  let offset = 0;
  let nodes = 0;
  const fail = (): never => {
    throw new Error('Invalid or unsupported JSON.');
  };
  const whitespace = () => {
    while (/[\t\n\r ]/.test(source[offset] ?? '\0')) offset++;
  };
  const string = (): string => {
    const start = offset++;
    while (offset < source.length) {
      const character = source[offset++];
      if (character === '"') {
        try {
          return JSON.parse(source.slice(start, offset)) as string;
        } catch {
          return fail();
        }
      }
      if (character === '\\') offset++;
    }
    return fail();
  };
  const value = (depth: number): JsonValue => {
    if (depth > limits.depth || ++nodes > limits.nodes) return fail();
    whitespace();
    const character = source[offset];
    if (character === '"') return string();
    if (character === '{') {
      offset++;
      const result: JsonObject = Object.create(null);
      const keys = new Set<string>();
      whitespace();
      if (source[offset] === '}') {
        offset++;
        return result;
      }
      while (offset < source.length) {
        whitespace();
        if (source[offset] !== '"') return fail();
        const key = string();
        if (keys.has(key)) return fail();
        keys.add(key);
        whitespace();
        if (source[offset++] !== ':') return fail();
        result[key] = value(depth + 1);
        whitespace();
        const separator = source[offset++];
        if (separator === '}') return result;
        if (separator !== ',') return fail();
      }
      return fail();
    }
    if (character === '[') {
      offset++;
      const result: JsonValue[] = [];
      whitespace();
      if (source[offset] === ']') {
        offset++;
        return result;
      }
      while (offset < source.length) {
        result.push(value(depth + 1));
        whitespace();
        const separator = source[offset++];
        if (separator === ']') return result;
        if (separator !== ',') return fail();
      }
      return fail();
    }
    for (const [token, literal] of [
      ['true', true],
      ['false', false],
      ['null', null],
    ] as const) {
      if (source.startsWith(token, offset)) {
        offset += token.length;
        return literal;
      }
    }
    const number = /^-?(?:0|[1-9]\d*)(?:\.\d+)?(?:[eE][+-]?\d+)?/.exec(source.slice(offset));
    if (!number) return fail();
    offset += number[0].length;
    const parsed = Number(number[0]);
    if (!Number.isFinite(parsed) || Math.abs(parsed) > Number.MAX_SAFE_INTEGER) return fail();
    return parsed;
  };
  const result = value(0);
  whitespace();
  if (offset !== source.length) return fail();
  return result;
}

export function isJsonObject(value: unknown): value is JsonObject {
  return !!value && typeof value === 'object' && !Array.isArray(value);
}

/** Local comparison only. Requests echo owner contract hashes, never a client-computed hash. */
export function stableJson(value: JsonValue): string {
  if (Array.isArray(value)) return `[${value.map(stableJson).join(',')}]`;
  if (isJsonObject(value))
    return `{${Object.keys(value)
      .sort()
      .map((key) => `${JSON.stringify(key)}:${stableJson(value[key])}`)
      .join(',')}}`;
  return JSON.stringify(value);
}
